import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Color from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'

function createToolbar(editor, wrapper) {
  const toolbar = document.createElement('div')
  toolbar.className = 'tiptap-toolbar'

  const groups = [
    [
      { label: '<b>B</b>', command: () => editor.chain().focus().toggleBold().run(), active: 'bold' },
      { label: '<i>I</i>', command: () => editor.chain().focus().toggleItalic().run(), active: 'italic' },
      { label: '<u>U</u>', command: () => editor.chain().focus().toggleUnderline().run(), active: 'underline' },
      { label: '<s>S</s>', command: () => editor.chain().focus().toggleStrike().run(), active: 'strike' },
    ],
    [
      { label: 'H1', command: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), active: 'heading', activeAttrs: { level: 1 } },
      { label: 'H2', command: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: 'heading', activeAttrs: { level: 2 } },
      { label: 'H3', command: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: 'heading', activeAttrs: { level: 3 } },
    ],
    [
      { label: '&#8226; List', command: () => editor.chain().focus().toggleBulletList().run(), active: 'bulletList' },
      { label: '1. List', command: () => editor.chain().focus().toggleOrderedList().run(), active: 'orderedList' },
      { label: 'Quote', command: () => editor.chain().focus().toggleBlockquote().run(), active: 'blockquote' },
      { label: 'Code', command: () => editor.chain().focus().toggleCodeBlock().run(), active: 'codeBlock' },
    ],
    [
      { label: 'Left', command: () => editor.chain().focus().setTextAlign('left').run(), active: 'textAlign', activeAttrs: { textAlign: 'left' } },
      { label: 'Center', command: () => editor.chain().focus().setTextAlign('center').run(), active: 'textAlign', activeAttrs: { textAlign: 'center' } },
      { label: 'Right', command: () => editor.chain().focus().setTextAlign('right').run(), active: 'textAlign', activeAttrs: { textAlign: 'right' } },
    ],
    [
      { label: '&#128279;', command: () => setLink(editor) },
      { label: '&#128247;', command: () => addImage(editor) },
    ],
    [
      { label: 'HR', command: () => editor.chain().focus().setHorizontalRule().run() },
      { label: 'Clear', command: () => editor.chain().focus().clearNodes().unsetAllMarks().run() },
    ],
    [
      { label: '&#8617;', command: () => editor.chain().focus().undo().run() },
      { label: '&#8618;', command: () => editor.chain().focus().redo().run() },
    ],
  ]

  groups.forEach((group, gi) => {
    const groupEl = document.createElement('span')
    groupEl.className = 'tiptap-toolbar-group'
    group.forEach(btn => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'tiptap-toolbar-btn'
      button.innerHTML = btn.label
      button.addEventListener('click', (e) => { e.preventDefault(); btn.command() })
      if (btn.active) {
        button.dataset.tiptapActive = btn.active
        if (btn.activeAttrs) button.dataset.tiptapActiveAttrs = JSON.stringify(btn.activeAttrs)
      }
      groupEl.appendChild(button)
    })
    toolbar.appendChild(groupEl)
  })

  const colorInput = document.createElement('input')
  colorInput.type = 'color'
  colorInput.className = 'tiptap-color-picker'
  colorInput.value = '#000000'
  colorInput.title = 'Text color'
  colorInput.addEventListener('input', (e) => {
    editor.chain().focus().setColor(e.target.value).run()
  })
  toolbar.appendChild(colorInput)

  wrapper.prepend(toolbar)

  editor.on('selectionUpdate', () => updateToolbarState(editor, toolbar))
  editor.on('update', () => updateToolbarState(editor, toolbar))
}

function updateToolbarState(editor, toolbar) {
  toolbar.querySelectorAll('[data-tiptap-active]').forEach(btn => {
    const name = btn.dataset.tiptapActive
    const attrs = btn.dataset.tiptapActiveAttrs ? JSON.parse(btn.dataset.tiptapActiveAttrs) : undefined
    btn.classList.toggle('is-active', editor.isActive(name, attrs))
  })
}

function setLink(editor) {
  const prev = editor.getAttributes('link').href
  const url = prompt('URL', prev || 'https://')
  if (url === null) return
  if (url === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  } else {
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}

function addImage(editor) {
  const url = prompt('Image URL')
  if (url) {
    editor.chain().focus().setImage({ src: url }).run()
  }
}

function initEditor(container) {
  const jsonInput = container.querySelector('[data-tiptap-json]')
  const htmlInput = container.querySelector('[data-tiptap-html]')
  const editorEl = container.querySelector('[data-tiptap-editable]')

  if (!editorEl || editorEl.dataset.tiptapInitialized) return

  let initialContent = ''
  if (jsonInput && jsonInput.value && jsonInput.value.trim() !== '') {
    try { initialContent = JSON.parse(jsonInput.value) } catch (_e) { initialContent = htmlInput ? htmlInput.value : '' }
  } else if (htmlInput && htmlInput.value) {
    initialContent = htmlInput.value
  }

  const editor = new Editor({
    element: editorEl,
    extensions: [
      StarterKit,
      Underline,
      Image,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      if (jsonInput) jsonInput.value = JSON.stringify(editor.getJSON())
      if (htmlInput) htmlInput.value = editor.getHTML()
    },
  })

  createToolbar(editor, container)

  if (jsonInput) jsonInput.value = JSON.stringify(editor.getJSON())
  if (htmlInput) htmlInput.value = editor.getHTML()

  editorEl.dataset.tiptapInitialized = 'true'
}

function initAllEditors() {
  document.querySelectorAll('[data-tiptap-wrapper]').forEach(initEditor)
}

document.addEventListener('turbo:load', initAllEditors)
document.addEventListener('DOMContentLoaded', initAllEditors)
